import { type ComputedRef, computed } from "vue";
import { useI18n } from "vue-i18n";
import type { DataListLabels } from "@/design/primitives/data-list";
import type { PaginationLabels } from "@/design/primitives/pagination";

/**
 * THE WORDS THE PRIMITIVES ASK FOR — said once, for the whole app.
 *
 * `design/` knows no locale: `Pagination`, `DataList` and `CopyRow` take their
 * sentences by prop with an English default, and the CALL SITE supplies them.
 * `shared/labels/primitive-labels` was the placeholder that held those sentences
 * in Portuguese-only until a screen learned to speak — its own comment said the
 * lot that teaches a screen i18n replaces `:labels="PT_BR_PAGINATION"` with
 * labels built from `t(...)` and drops the import, and that when the last import
 * goes so does the file. This is that replacement, and the file is now gone.
 *
 * ── WHY IT LIVES IN `shared/` AND NOT IN `pages/` ─────────────────────────
 *
 * It was born in `pages/i18n/tables.ts`, which served the eight screens in that
 * folder. But sixteen call sites wanted these same eight words, and eleven of
 * them are in `features/` or `dynamic/` — neither of which may import from
 * `pages/`. Reaching them from there was impossible, so each would have grown its
 * own copy: sixteen translations of "Previous" that drift, which is the exact
 * failure the placeholder was written to prevent. `shared/` is the one shelf
 * every layer may take from, so the words moved to it rather than multiplying.
 *
 * ── WHY NOT `shared/i18n/common.ts` ───────────────────────────────────────
 *
 * `common.ts` sits in the ROOT composer: a screen writes `t('action.cancel')` and
 * gets it without importing anything. These eight do not want that. They are not
 * read by a screen at all — they are assembled into three LABEL BAGS and handed
 * to a primitive as a prop, which is a different shape from a word a template
 * says. Keeping them in their own local dictionary also keeps `pagination.of`
 * from colliding with a feature's own `pagination.*`, which billing has.
 */
export const tableMessages = {
	en: {
		pagination: {
			nav: "Pagination",
			of: "of",
			perPage: "Per page",
			previous: "Previous",
			next: "Next",
			page: "Page",
			goToPage: "Go to page",
			goTo: "Go to",
		},
		dataList: {
			selectAll: "Select all on this page",
			select: "Select",
			actions: "Actions",
			onThisPage: "on this page",
		},
		// Lower case on purpose: these sit at 10px next to a field label, where a
		// capital reads as a second title.
		copyRow: { copyLabel: "copy", copiedLabel: "copied", openLabel: "open" },
	},
	"pt-BR": {
		pagination: {
			nav: "Paginação",
			of: "de",
			perPage: "Por página",
			previous: "Anterior",
			next: "Próxima",
			page: "Página",
			goToPage: "Ir para a página",
			goTo: "Ir para",
		},
		dataList: {
			selectAll: "Selecionar todos desta página",
			select: "Selecionar",
			actions: "Ações",
			onThisPage: "nesta página",
		},
		copyRow: { copyLabel: "copiar", copiedLabel: "copiado", openLabel: "abrir" },
	},
	el: {
		pagination: {
			nav: "Σελιδοποίηση",
			of: "από",
			perPage: "Ανά σελίδα",
			previous: "Προηγούμενη",
			next: "Επόμενη",
			page: "Σελίδα",
			goToPage: "Μετάβαση στη σελίδα",
			goTo: "Μετάβαση",
		},
		dataList: {
			selectAll: "Επιλογή όλων σε αυτή τη σελίδα",
			select: "Επιλογή",
			actions: "Ενέργειες",
			onThisPage: "σε αυτή τη σελίδα",
		},
		copyRow: { copyLabel: "αντιγραφή", copiedLabel: "αντιγράφηκε", openLabel: "άνοιγμα" },
	},
	es: {
		pagination: {
			nav: "Paginación",
			of: "de",
			perPage: "Por página",
			previous: "Anterior",
			next: "Siguiente",
			page: "Página",
			goToPage: "Ir a la página",
			goTo: "Ir a",
		},
		dataList: {
			selectAll: "Seleccionar todo en esta página",
			select: "Seleccionar",
			actions: "Acciones",
			onThisPage: "en esta página",
		},
		copyRow: { copyLabel: "copiar", copiedLabel: "copiado", openLabel: "abrir" },
	},
	fr: {
		pagination: {
			nav: "Pagination",
			of: "sur",
			perPage: "Par page",
			previous: "Précédent",
			next: "Suivant",
			page: "Page",
			goToPage: "Aller à la page",
			goTo: "Aller à",
		},
		dataList: {
			selectAll: "Tout sélectionner sur cette page",
			select: "Sélectionner",
			actions: "Actions",
			onThisPage: "sur cette page",
		},
		copyRow: { copyLabel: "copier", copiedLabel: "copié", openLabel: "ouvrir" },
	},
	it: {
		pagination: {
			nav: "Impaginazione",
			of: "di",
			perPage: "Per pagina",
			previous: "Precedente",
			next: "Successiva",
			page: "Pagina",
			goToPage: "Vai alla pagina",
			goTo: "Vai a",
		},
		dataList: {
			selectAll: "Seleziona tutto in questa pagina",
			select: "Seleziona",
			actions: "Azioni",
			onThisPage: "in questa pagina",
		},
		copyRow: { copyLabel: "copia", copiedLabel: "copiato", openLabel: "apri" },
	},
	de: {
		pagination: {
			nav: "Seitennummerierung",
			of: "von",
			perPage: "Pro Seite",
			previous: "Zurück",
			next: "Weiter",
			page: "Seite",
			goToPage: "Zur Seite gehen",
			goTo: "Gehe zu",
		},
		dataList: {
			selectAll: "Alle auf dieser Seite auswählen",
			select: "Auswählen",
			actions: "Aktionen",
			onThisPage: "auf dieser Seite",
		},
		copyRow: { copyLabel: "kopieren", copiedLabel: "kopiert", openLabel: "öffnen" },
	},
} as const;

export interface CopyRowLabels {
	readonly copyLabel: string;
	readonly copiedLabel: string;
	readonly openLabel: string;
}

/**
 * The three label bags, reactive to the panel's language.
 *
 * They are `computed` and not plain objects because switching the language is
 * immediate (`setPanelLocale`) — a bag built once at setup would leave a table's
 * footer in the old language until the next navigation.
 *
 * Each field is resolved through `t()` one at a time rather than lifted whole
 * with `tm()`: `tm` hands back the message TREE, whose leaves are compiled
 * message functions and not strings, and a primitive that renders one would print
 * a function. Spelling the keys out also means a key deleted from the dictionary
 * is a typecheck failure here instead of a blank label on screen.
 *
 * ── WHY `isolated` AND NOT `local` ────────────────────────────────────────
 *
 * A component may hold exactly ONE local scope. `useI18n({ useScope: "local" })`
 * caches its composer on the component instance: called a second time it hands
 * back the FIRST one and drops the second bag of messages on the floor (vue-i18n
 * says `DUPLICATE_USE_I18N_CALLING` and carries on). Every page here mounts its
 * own dictionary and then calls this — so with `local` the eight words resolved
 * against the PAGE's dictionary, found nothing, and the footer drew
 * `pagination.previous` where "Anterior" belongs.
 *
 * `isolated` is the scope made for exactly this: an independent composer, not
 * tied to the component instance, that still inherits the panel's language from
 * the root. `two-scopes.spec.ts` beside this file is the proof.
 */
export function useTableLabels(): {
	pagination: ComputedRef<PaginationLabels>;
	dataList: ComputedRef<DataListLabels>;
	copyRow: ComputedRef<CopyRowLabels>;
} {
	const { t } = useI18n({ useScope: "isolated", inheritLocale: true, messages: tableMessages });
	return {
		pagination: computed<PaginationLabels>(() => ({
			nav: t("pagination.nav"),
			of: t("pagination.of"),
			perPage: t("pagination.perPage"),
			previous: t("pagination.previous"),
			next: t("pagination.next"),
			page: t("pagination.page"),
			goToPage: t("pagination.goToPage"),
			goTo: t("pagination.goTo"),
		})),
		dataList: computed<DataListLabels>(() => ({
			selectAll: t("dataList.selectAll"),
			select: t("dataList.select"),
			actions: t("dataList.actions"),
			onThisPage: t("dataList.onThisPage"),
		})),
		copyRow: computed<CopyRowLabels>(() => ({
			copyLabel: t("copyRow.copyLabel"),
			copiedLabel: t("copyRow.copiedLabel"),
			openLabel: t("copyRow.openLabel"),
		})),
	};
}
